import { useRef, type ComponentType } from 'react';
import { Field, Form as ReactFinalForm, FormSpy } from 'react-final-form';
import { formatUtcDateTimeToTimeZone } from '@belong/common';
import { IconSpinner } from '@belong/icons';
import { Button, Modal, Text } from '@belong/ui';
import { AvailabilityPicker } from 'components/AvailabilityPicker/availability-picker';
import { CheckboxFinalFormAdapter } from 'components/Checkbox/Checkbox';
import { InputFinalFormAdapter } from 'components/Input/Input';
import {
  SELECTOR_TYPES,
  SelectorFinalFormAdapter,
} from 'components/Selector/Selector';
import { YesNoFinalFormAdapter } from 'components/Selector/YesNoFinalFormAdapter/YesNoFinalFormAdapter';
import type {
  AppointmentOutcomeModalProps,
  FormValues,
} from './appointment-outcome-modal.types';
import {
  ARRIVAL_STATUS_OPTIONS,
  initialValues,
  validate,
} from './appointment-outcome-modal.form';
import { useAppointmentOutcomeSubmit } from './appointment-outcome-modal.hooks';
import { StarRatingField } from './star-rating-field';

export function AppointmentOutcomeModal({
  isOpen,
  onDismiss,
  maintenanceUniqueId,
  appointmentUniqueId,
  partnerName,
  scheduledStartOn,
  timeZone,
}: AppointmentOutcomeModalProps) {
  const initialFocusRef = useRef<HTMLDivElement>(null);
  const { isSubmitting, handleSubmit } = useAppointmentOutcomeSubmit({
    maintenanceUniqueId,
    appointmentUniqueId,
    onDone: onDismiss,
  });

  return (
    <>
      <Modal
        isOpen={isOpen}
        name="appointment_outcome"
        onDismiss={onDismiss}
        preventDismissOnOutsideClick
        size="auto"
        enableOverflow
        overlayClassName="z-fullscreen"
        contentClassName="sm:w-[730px] md:w-[730px]"
        initialFocusRef={initialFocusRef}
      >
        <div ref={initialFocusRef} tabIndex={-1} className="outline-none" />
        <div className="flex flex-col px-2sm pt-3xl sm:px-lg sm:pt-2xl">
          <div className="mb-2xl text-center">
            <Text variant="h2" fontWeight="semibold">
              How did the visit go?
            </Text>
            {scheduledStartOn && (
              <Text className="mt-xs text-center">
                {formatUtcDateTimeToTimeZone({
                  dateTime: scheduledStartOn,
                  format: 'MMMM do, yyyy',
                  timeZone,
                })}{' '}
                {formatUtcDateTimeToTimeZone({
                  dateTime: scheduledStartOn,
                  format: 'h:mm a z',
                  timeZone,
                })}
              </Text>
            )}
          </div>
          <ReactFinalForm<FormValues>
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validate={validate}
          >
            {({ handleSubmit: submitForm, invalid, form }) => (
              <form onSubmit={submitForm}>
                <div className="flex flex-col items-center gap-2xl pb-2xl">
                  {isSubmitting ? (
                    <div className="flex items-center justify-center py-2xl">
                      <IconSpinner width={40} />
                    </div>
                  ) : (
                    <>
                      <div className="w-full">
                        <Text
                          fontWeight="semibold"
                          className="mb-sm"
                          variant="h3"
                        >
                          Did{' '}
                          <span className="text-green">
                            {partnerName ?? 'the Pro'}
                          </span>{' '}
                          arrive on time?
                        </Text>
                        <Field
                          name="arrivalStatus"
                          component={SelectorFinalFormAdapter}
                          buttons={ARRIVAL_STATUS_OPTIONS}
                          type={SELECTOR_TYPES.SMALLTEXTBUTTONWIDTH}
                          fluid
                        />
                      </div>

                      <FormSpy<FormValues> subscription={{ values: true }}>
                        {({ values }) => {
                          const isResolvableBranch =
                            values.arrivalStatus === 'onTime' ||
                            values.arrivalStatus === 'arrivedLate';
                          const isNeverArrived =
                            values.arrivalStatus === 'neverArrived';
                          const isInReschedulePath =
                            (isResolvableBranch && values.resolved === false) ||
                            isNeverArrived;

                          return (
                            <>
                              {isResolvableBranch && (
                                <div className="w-full">
                                  <Text
                                    fontWeight="semibold"
                                    className="mb-sm"
                                    variant="h3"
                                  >
                                    Was the issue fully resolved?
                                  </Text>
                                  <Field
                                    name="resolved"
                                    component={YesNoFinalFormAdapter}
                                    type={SELECTOR_TYPES.SMALLTEXTBUTTONWIDTH}
                                    fluid
                                  />
                                </div>
                              )}

                              {isResolvableBranch &&
                                values.resolved === true && (
                                  <div className="w-full">
                                    <Text
                                      fontWeight="semibold"
                                      className="mb-sm"
                                      variant="h3"
                                    >
                                      How did the Belong Pro do?
                                    </Text>
                                    <StarRatingField name="workRating" />

                                    <Field
                                      name="workComments"
                                      component={InputFinalFormAdapter}
                                      placeholder="Anything you want us to know?"
                                      textarea
                                      maxLength={200}
                                    />
                                  </div>
                                )}

                              {(isNeverArrived ||
                                values.resolved === false) && (
                                <div className="w-full rounded border border-gray border-solid bg-light-gray px-sm py-sm">
                                  <Text fontWeight="semibold">
                                    We&apos;re sorry the Pro didn&apos;t make
                                    it. We&apos;ll take care of rescheduling
                                    right away, thanks for letting us know.
                                  </Text>
                                </div>
                              )}

                              {isInReschedulePath && (
                                <div className="w-full">
                                  {!values.noLongerNeedsVisit && (
                                    <AvailabilityPicker
                                      timeZone={timeZone}
                                      initialSelections={
                                        values.requestorAvailability
                                      }
                                      onChange={(selections) =>
                                        form.change(
                                          'requestorAvailability',
                                          selections,
                                        )
                                      }
                                    />
                                  )}

                                  <div className="w-full flex justify-end mt-xl">
                                    <Field
                                      name="noLongerNeedsVisit"
                                      // Cast because this is react-final-form's
                                      // own `Field`, whose `component` is typed
                                      // against `FieldRenderProps`; the adapter
                                      // takes a looser prop bag it forwards on.
                                      component={
                                        CheckboxFinalFormAdapter as ComponentType<any>
                                      }
                                      label="I NO LONGER NEED THIS VISIT"
                                      useOnChangeFormToHandle
                                      alignWithField={false}
                                      onClickCustom={() =>
                                        form.change('requestorAvailability', [])
                                      }
                                    />
                                  </div>
                                </div>
                              )}
                            </>
                          );
                        }}
                      </FormSpy>
                    </>
                  )}

                  <Button
                    type="submit"
                    size={{ _: 'fluid', lg: 'default' }}
                    disabled={invalid || isSubmitting}
                  >
                    Submit
                  </Button>
                </div>
              </form>
            )}
          </ReactFinalForm>
        </div>
      </Modal>
    </>
  );
}
